import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Link } from "react-router-dom";
import BrandMark from "../components/brand/BrandMark";
import {
  Plus, Save, Upload, Trash2, Star, CheckCircle2, AlertTriangle, History, Globe, Loader2, ArrowLeft,
} from "lucide-react";

const API = "/api";
const PREVIEW_COUNTRIES = ["PE", "US", "BB", "BR", "ES", "IN"];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("avisocar_token")}` });

const PlanRow = ({ plan, onChange, onSave, onDelete, saving }) => (
  <Card className="border-white/10 bg-[#0B2545]" data-testid={`plan-row-${plan.code}`}>
    <CardContent className="p-5">
      <div className="grid gap-4 md:grid-cols-[1.2fr_0.8fr_0.8fr_0.8fr_auto]">
        <div>
          <Label className="text-xs text-slate-500">Nombre público (ES)</Label>
          <Input
            value={(plan.names || {}).es || ""}
            onChange={(e) => onChange({ ...plan, names: { ...(plan.names || {}), es: e.target.value } })}
            className="mt-1 border-white/15 bg-[#14375F] text-white"
            data-testid={`plan-name-${plan.code}`}
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Duración (meses)</Label>
          <Input
            type="number"
            min="1"
            value={plan.duration_months}
            onChange={(e) => onChange({ ...plan, duration_months: Number(e.target.value) })}
            className="mt-1 border-white/15 bg-[#14375F] text-white"
            data-testid={`plan-duration-${plan.code}`}
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Cobro real (PEN)</Label>
          <Input
            type="number"
            min="1"
            step="1"
            value={plan.charge_amount}
            onChange={(e) => onChange({ ...plan, charge_amount: Number(e.target.value) })}
            className="mt-1 border-white/15 bg-[#14375F] text-white"
            data-testid={`plan-amount-${plan.code}`}
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Orden</Label>
          <Input
            type="number"
            value={plan.sort_order}
            onChange={(e) => onChange({ ...plan, sort_order: Number(e.target.value) })}
            className="mt-1 border-white/15 bg-[#14375F] text-white"
            data-testid={`plan-order-${plan.code}`}
          />
        </div>
        <div className="flex items-end gap-2">
          <Button
            size="sm"
            onClick={() => onSave(plan)}
            disabled={saving}
            className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]"
            data-testid={`plan-save-${plan.code}`}
          >
            <Save className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(plan)}
            className="text-red-400 hover:bg-red-500/10"
            data-testid={`plan-delete-${plan.code}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={plan.active ? "default" : "outline"}
          onClick={() => onSave({ ...plan, active: !plan.active })}
          className={plan.active ? "bg-emerald-600 text-white hover:bg-emerald-700" : "border-white/20 text-slate-600"}
          data-testid={`plan-active-${plan.code}`}
        >
          {plan.active ? "Activo" : "Inactivo"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onSave({ ...plan, is_default: true })}
          className={`border-white/20 ${plan.is_default ? "text-[#5BC0FF]" : "text-slate-600"}`}
          data-testid={`plan-default-${plan.code}`}
        >
          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
          {plan.is_default ? "Plan por defecto" : "Marcar por defecto"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onSave({ ...plan, featured: !plan.featured })}
          className={`border-white/20 ${plan.featured ? "text-amber-400" : "text-slate-600"}`}
          data-testid={`plan-featured-${plan.code}`}
        >
          <Star className="mr-1 h-3.5 w-3.5" />
          {plan.featured ? "Destacado" : "Destacar"}
        </Button>
        <span className="ml-auto text-xs text-slate-500">código: {plan.code}</span>
      </div>
    </CardContent>
  </Card>
);

const AdminMembershipPlans = () => {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewCountry, setPreviewCountry] = useState("PE");
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/membership/plans`, { headers: authHeaders() });
    setData(r.data);
    setDrafts(r.data.drafts || []);
  }, []);

  const loadPreview = useCallback(async (iso) => {
    const r = await axios.get(`${API}/admin/membership/plans/preview?country_code=${iso}`, { headers: authHeaders() });
    setPreview(r.data);
  }, []);

  useEffect(() => { load().catch(() => toast.error("No se pudo cargar los planes")); }, [load]);
  useEffect(() => { loadPreview(previewCountry).catch(() => setPreview(null)); }, [previewCountry, loadPreview, data]);

  const savePlan = async (plan) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/membership/plans/${plan.plan_id}`, {
        code: plan.code,
        duration_months: Number(plan.duration_months),
        charge_amount: Number(plan.charge_amount),
        names: plan.names,
        active: plan.active,
        is_default: plan.is_default,
        featured: plan.featured,
        sort_order: Number(plan.sort_order),
      }, { headers: authHeaders() });
      toast.success("Borrador guardado — publica para aplicarlo");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const addPlan = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/membership/plans`, {
        duration_months: 3,
        charge_amount: 30,
        names: { es: "3 meses", en: "3 months", pt: "3 meses", fr: "3 mois" },
        sort_order: 5,
      }, { headers: authHeaders() });
      toast.success("Plan creado como borrador");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(`¿Eliminar el borrador "${(plan.names || {}).es || plan.code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/membership/plans/${plan.plan_id}`, { headers: authHeaders() });
      toast.success("Borrador eliminado — publica para aplicarlo");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const publish = async () => {
    if (!window.confirm("Publicar la configuración actual. Se aplicará SOLO a compras nuevas. ¿Continuar?")) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/membership/plans/publish`, {}, { headers: authHeaders() });
      toast.success(`Publicado — versión ${r.data.config_version}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo publicar");
    } finally {
      setSaving(false);
    }
  };

  const republish = async (version) => {
    if (!window.confirm(`Republicar la versión ${version} como una versión NUEVA. El histórico no se modifica. ¿Continuar?`)) return;
    try {
      const r = await axios.post(`${API}/admin/membership/plans/publish`, { from_version: version }, { headers: authHeaders() });
      toast.success(`Publicado — versión ${r.data.config_version}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo republicar");
    }
  };

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#071B2F] text-slate-500" data-testid="plans-loading">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando planes…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071B2F] px-4 py-6 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-6xl space-y-6" data-testid="admin-membership-plans">
      <div className="flex items-center justify-between gap-4">
        <BrandMark size="sm" to="/admin" onDark />
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#5BC0FF]" data-testid="plans-back-link">
          <ArrowLeft className="h-4 w-4" /> Volver al panel
        </Link>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Planes de membresía</h1>
          <p className="mt-1 text-sm text-slate-500">
            Fuente única para la web y para las apps Android/iOS. {data.notice}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-[#14375F] text-[#5BC0FF]" data-testid="plans-published-version">
            Versión publicada v{data.published?.config_version ?? 0}
          </Badge>
          <Button onClick={addPlan} variant="outline" className="border-white/20 text-slate-600" data-testid="plans-add-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Añadir plan
          </Button>
          <Button onClick={publish} disabled={saving} className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]" data-testid="plans-publish-btn">
            <Upload className="mr-1.5 h-4 w-4" /> Publicar
          </Button>
        </div>
      </div>

      {data.has_unpublished_changes && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="plans-dirty-banner">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
          <p className="text-sm text-amber-700">
            Hay cambios en borrador sin publicar. Los clientes siguen viendo la versión v{data.published?.config_version ?? 0} hasta que publiques.
          </p>
        </div>
      )}

      {data.published?.legal_review_required && (
        <div className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4" data-testid="plans-legal-warning">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
          <p className="text-sm text-red-700">
            <strong>LEGAL_CONTENT_REVIEW_REQUIRED</strong> — los documentos legales publicados mencionan un precio distinto al plan
            vigente. Revísalos en “Documentos legales”; AvisoCar no los reescribe automáticamente.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {drafts.map((plan, i) => (
          <PlanRow
            key={plan.plan_id}
            plan={plan}
            saving={saving}
            onChange={(next) => setDrafts(drafts.map((p, idx) => (idx === i ? next : p)))}
            onSave={savePlan}
            onDelete={deletePlan}
          />
        ))}
      </div>

      <Card className="border-white/10 bg-[#0B2545]">
        <CardContent className="p-5">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Globe className="h-4 w-4 text-[#5BC0FF]" />
            <h2 className="text-base font-semibold text-white">Vista previa del cliente (web / Android / iOS)</h2>
            <select
              value={previewCountry}
              onChange={(e) => setPreviewCountry(e.target.value)}
              className="ml-auto h-9 rounded-lg border border-white/15 bg-[#14375F] px-2 text-sm text-white"
              data-testid="plans-preview-country"
            >
              {PREVIEW_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {preview ? (
            <div className="space-y-2" data-testid="plans-preview-body">
              <p className="text-xs text-slate-500">
                Moneda de cobro {preview.actual_charge_currency} · moneda mostrada {preview.display_currency}
                {preview.display_is_charge_currency ? " (es la moneda de cobro)" : " (estimación aproximada)"}
              </p>
              {preview.plans.map((p) => (
                <div key={p.plan_id} className="flex flex-wrap items-center gap-3 rounded-lg bg-[#14375F] px-3 py-2 text-sm text-white">
                  <span className="font-semibold">{p.name}</span>
                  <span className="text-[#5BC0FF]">{p.actual_charge.symbol} {p.actual_charge.amount} {p.actual_charge.currency}</span>
                  {p.display_estimate
                    ? <span className="text-slate-600">≈ {p.display_estimate.symbol} {p.display_estimate.amount} aprox.</span>
                    : <span className="text-slate-500">sin estimación local → se muestra el precio en PEN</span>}
                  {p.is_default && <Badge className="bg-[#1B84FF] text-white">por defecto</Badge>}
                  {p.featured && <Badge className="bg-amber-500 text-white">destacado</Badge>}
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-500">Sin datos de vista previa.</p>}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-[#0B2545]">
        <CardContent className="p-5">
          <div className="mb-4 flex items-center gap-2">
            <History className="h-4 w-4 text-[#5BC0FF]" />
            <h2 className="text-base font-semibold text-white">Historial de versiones</h2>
          </div>
          <div className="space-y-2" data-testid="plans-history">
            {(data.history || []).map((v) => (
              <div key={v.id} className="flex flex-wrap items-center gap-3 rounded-lg bg-[#14375F] px-3 py-2 text-sm text-white">
                <span className="font-semibold">v{v.config_version}</span>
                <span className="text-slate-600">{new Date(v.published_at).toLocaleString("es-PE")}</span>
                <span className="text-slate-600">{(v.plans || []).map((p) => `${p.names?.es}: S/ ${p.charge_amount}`).join(" · ")}</span>
                {v.republished_from && <Badge className="bg-[#1B84FF] text-white">republicada de v{v.republished_from}</Badge>}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => republish(v.config_version)}
                  className="ml-auto text-[#5BC0FF] hover:bg-white/10"
                  data-testid={`plans-republish-${v.config_version}`}
                >
                  Republicar
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
    </div>
  );
};

export default AdminMembershipPlans;
